import { getConfig } from "../config/app.config";
import { getBaseUrl as getFrameworkBaseUrl } from "../config/api.config";

/**
 * Generic API client utility that handles authentication and base URL consistently
 * Following the same pattern as backend analytics service with getAuthToken callback
 */
export class ApiClient {
  private static instance: ApiClient;
  private getAuthToken?: () => Promise<string>;
  
  private constructor() {}
  
  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  /**
   * Set the auth token provider function
   * This should be called during app initialization with React Admin's authProvider
   * 
   * Example:
   * ```
   * const authProvider = useAuthProvider();
   * apiClient.setAuthProvider(async () => {
   *   const token = await authProvider.getIdentity();
   *   return token?.token || '';
   * });
   * ```
   */
  setAuthProvider(getAuthToken: () => Promise<string>): void {
    this.getAuthToken = getAuthToken;
    console.log('🔐 [API Client] Auth provider configured');
  }

  /**
   * Get base URL using the same logic as dataProvider
   */
  getBaseUrl(): string {
    // Try to get from app config first (existing behavior)
    const configUrl = getConfig('VITE_BASE_URL');
    if (configUrl) {
      return configUrl;
    }
    
    // Fallback to framework utility for consistent URL resolution
    return getFrameworkBaseUrl();
  }

  /**
   * Build authenticated headers (following backend analytics pattern)
   */
  private async buildAuthenticatedHeaders(): Promise<Headers> {
    const headers = new Headers({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    console.log('🔐 [API Client] Building authenticated headers');
    console.log('🔐 [API Client] getAuthToken:', this.getAuthToken);

    // Add bearer token if available via auth provider callback
    if (this.getAuthToken) {
      try {
        const token = await this.getAuthToken();
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
          console.log('🔐 [API Client] Added bearer token from auth provider');
        }
      } catch (error) {
        console.error('🔐 [API Client] Failed to get auth token from provider:', error);
      }
    } else {
      // Fallback to localStorage/sessionStorage for backwards compatibility
      try {
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
          console.log('🔐 [API Client] Added bearer token from storage (fallback)');
        } else {
          console.warn('🔐 [API Client] No auth provider configured and no token in storage');
        }
      } catch (error) {
        console.error('🔐 [API Client] Failed to get auth token from storage:', error);
      }
    }

    return headers;
  }

  /**
   * Generic API call method
   */
  async call<T = any>(
    endpoint: string, 
    options: {
      method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
      body?: any;
      headers?: Record<string, string>;
    } = {}
  ): Promise<T> {
    const { method = 'GET', body, headers: customHeaders = {} } = options;

    console.log('🔐 [API Client] Calling endpoint:', endpoint);
    
    // Build full URL
    const baseUrl = this.getBaseUrl();
    const fullUrl = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint}`;
    
    console.log(`📡 [API Client] ${method} ${fullUrl}`);
    
    try {
      // Build headers with auth
      const headers = await this.buildAuthenticatedHeaders();
      
      // Add custom headers
      Object.entries(customHeaders).forEach(([key, value]) => {
        headers.set(key, value);
      });
      
      // Make request
      const response = await fetch(fullUrl, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined
      });
      
      console.log(`📡 [API Client] Response: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log(`✅ [API Client] Success:`, result);
      
      return result;
      
    } catch (error) {
      console.error(`❌ [API Client] Failed ${method} ${fullUrl}:`, error);
      throw error;
    }
  }

  /**
   * Upload file with multipart/form-data
   * Don't set Content-Type - browser will set it with boundary
   */
  async uploadFile<T = any>(endpoint: string, formData: FormData): Promise<T> {
    console.log('📤 [API Client] Uploading file to:', endpoint);
    
    // Build full URL
    const baseUrl = this.getBaseUrl();
    const fullUrl = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint}`;
    
    console.log(`📡 [API Client] POST ${fullUrl} (multipart/form-data)`);
    
    try {
      // Build headers WITHOUT Content-Type (browser will add it with boundary for FormData)
      const headers = new Headers();
      
      // Add auth token
      if (this.getAuthToken) {
        try {
          const token = await this.getAuthToken();
          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
            console.log('🔐 [API Client] Added bearer token for file upload');
          }
        } catch (error) {
          console.error('🔐 [API Client] Failed to get auth token:', error);
        }
      } else {
        // Fallback to storage
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
          console.log('🔐 [API Client] Added bearer token from storage');
        }
      }
      
      // Make request (browser auto-sets Content-Type with boundary for FormData)
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        body: formData
      });
      
      console.log(`📡 [API Client] Response: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [API Client] Upload failed:`, errorText);
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log(`✅ [API Client] File upload success:`, result);
      
      return result;
      
    } catch (error) {
      console.error(`❌ [API Client] File upload failed:`, error);
      throw error;
    }
  }

  /**
   * Convenience methods
   */
  async get<T = any>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.call<T>(endpoint, { method: 'GET', headers });
  }

  async post<T = any>(endpoint: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.call<T>(endpoint, { method: 'POST', body, headers });
  }

  async put<T = any>(endpoint: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.call<T>(endpoint, { method: 'PUT', body, headers });
  }

  async delete<T = any>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.call<T>(endpoint, { method: 'DELETE', headers });
  }
}

// Export singleton instance
export const apiClient = ApiClient.getInstance();
